/**
 * Instruments and connections as they come back over the native bridge.
 *
 * All of this is veriform-desktop's state, not this app's. An instrument is
 * configured on the user's machine and has no record here; a connection lives
 * in the workflow engine, scoped by this procedure's id. Nothing in this file
 * is persisted -- it is what the page learns by asking.
 *
 * Declared here rather than shared, for the same reason the bridge contract is
 * duplicated: there is no package between the two repositories. These shapes
 * mirror `engineConnectionTypes.ts` in veriform-desktop.
 */

/** veriform-desktop's INSTRUMENT_TYPE. Only the cases this page acts on differ. */
export enum DESKTOP_INSTRUMENT_TYPE {
  VISA = 'visa',
  SOFTWARE = 'software',
  ITEM = 'item',
  CAMERA = 'camera',
  MANUAL_CONTROL = 'manual_control',
}

/** veriform-desktop's INSTRUMENT_STATE, as the engine reports a provider. */
export enum DESKTOP_INSTRUMENT_STATE {
  RUNNING = 'running',
  STOPPED = 'stopped',
  ERROR = 'error',
  CREATED = 'created',
}

export interface DesktopInstrument {
  id: string;
  instrumentName: string;
  title: string;
  manufacturer: string;
  model: string;
  serialNumber: string;
  instrumentType: DESKTOP_INSTRUMENT_TYPE;
  category: string;
  providerState?: DESKTOP_INSTRUMENT_STATE;
  error: string;
  /** The swatch veriform-desktop shows this instrument with. */
  color: string;
}

/**
 * The reply to `engine.addConnection`. `started` can be false without the call
 * failing: the engine answers with the provider's state, and a provider can
 * refuse to start.
 */
export interface DesktopConnectionReply {
  equipmentId: string;
  started: boolean;
  providerState?: DESKTOP_INSTRUMENT_STATE;
  error?: string;
}

export interface AddConnectionRequest {
  procedureId: string;
  equipmentId: string;
  equipmentIdentifier: string;
  instrumentId: string;
  connectionConfig: string;
  /**
   * False records the assignment and its configuration without starting the
   * provider -- Confirm, as against Confirm and connect.
   */
  start?: boolean;
}

export interface RemoveConnectionRequest {
  procedureId: string;
  equipmentId: string;
  equipmentIdentifier: string;
}

/**
 * What a procedure's equipment is assigned to, and whether the engine is
 * holding that connection right now.
 *
 * `instrumentId` comes from veriform-desktop's own record, which is what
 * survives this page reloading; `connected` comes from the engine, which is the
 * only thing that knows whether the provider is up. They differ after a desktop
 * restart: the assignment is remembered, the connection is not.
 */
export interface DesktopConnection {
  equipmentId: string;
  instrumentId: string;
  connected: boolean;
  /** What was last sent as this connection's configuration. */
  configuration: string;
}

/**
 * Manual control is driven by a person, so there is no provider to start and
 * the engine is never told about it -- which also means it never comes back
 * from `engine.listConnections`. An equipment item assigned one is ready as
 * soon as it is assigned.
 */
export function needsEngineConnection(instrument?: DesktopInstrument): boolean {
  return Boolean(instrument) && instrument?.instrumentType !== DESKTOP_INSTRUMENT_TYPE.MANUAL_CONTROL;
}

/**
 * A provider's connection configuration, as veriform-desktop describes it.
 *
 * Which fields exist, which must be filled and which take a fixed set of values
 * are the provider's answer, so none of this is known here until it is asked
 * for. Mirrors `ProviderConfigSchema` in veriform-desktop.
 */
export interface ProviderConfigSchema {
  /** Field key to the provider's example value. */
  connectionConfig: Record<string, string>;
  /** Field key to its schema, which is where a field's `enum` comes from. */
  fullConnectionSchema: Record<string, { enum?: string[] }>;
  /** Field keys that must be filled, with no `connection_config.` prefix. */
  requiredFields: string[];
}
